import {
  Box,
  CircularProgress,
  Dialog,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core'
import { Button } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import {
  shopifyGetSalesChannels,
  ShopifySalesChannel,
  shopifyUpdatePriceIncrease,
  shopifyUpdateSalesChannels,
} from 'lib/api'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { Alert } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { SalesChannelsConnector } from '../account/SalesChannelConnector'

interface ShopifyActiveBoxProps {
  shop: string
}

export const ShopifyActiveBox = ({ shop }: ShopifyActiveBoxProps) => {
  const { user } = useUser()
  const { t } = useTranslation()

  const shopData = user.assignedShopifyShops.find((i) => i.shop == shop)
  const [priceModifierValue, setPriceModifierValue] = useState<string>(
    shopData!.priceModifier ? String(shopData!.priceModifier) : '0'
  )

  const [currPriceModifierValue, setCurrPriceModifierValue] = useState<string>('0')
  const [openPriceModifierDialog, setOpenPriceModifierDialog] = useState<boolean>(false)

  const { enqueueSnackbar } = useSnackbar()
  const [openSalesChannelsDialog, setOpenSalesChannelsDialog] = useState<boolean>(false)
  const [salesChannelsLoading, setSalesChannelsLoading] = useState<boolean>(false)
  const [salesChannels, setSalesChannels] = useState<ShopifySalesChannel[]>([])

  const getUninstallLink = (shop: string) => {
    return `https://admin.shopify.com/store/${
      shop.split('.')[0]
    }/settings/apps/app_installations/app/${process.env.REACT_APP_SHOPIFY_APP_NAME}`
  }

  const updatePriceModifier = async () => {
    await shopifyUpdatePriceIncrease(shop, parseInt(priceModifierValue) || 0)
    setOpenPriceModifierDialog(false)
  }

  const openDialog = async () => {
    setCurrPriceModifierValue(priceModifierValue)
    setOpenPriceModifierDialog(true)
  }

  const closeDialog = async () => {
    setPriceModifierValue(currPriceModifierValue)
    setOpenPriceModifierDialog(false)
  }

  const openSalesChannels = async () => {
    setOpenSalesChannelsDialog(true)
    setSalesChannelsLoading(true)
    try {
      const { channels } = await shopifyGetSalesChannels(shop)
      setSalesChannels(channels)
    } catch (e) {
      enqueueSnackbar(t('account.settings.shopify.salesChannelsError'), { variant: 'error' })
      setOpenSalesChannelsDialog(false)
    } finally {
      setSalesChannelsLoading(false)
    }
  }

  const toggleSalesChannel = (id: string) => {
    setSalesChannels(
      salesChannels.map((channel) =>
        channel.id === id ? { ...channel, selected: !channel.selected } : channel
      )
    )
  }

  const updateSalesChannels = async () => {
    try {
      await shopifyUpdateSalesChannels(
        shop,
        salesChannels.filter((channel) => channel.selected).map((channel) => channel.id)
      )
      setOpenSalesChannelsDialog(false)
    } catch (e) {
      enqueueSnackbar(t('account.settings.shopify.saveError'), { variant: 'error' })
    }
  }

  const salesChannelsDialog = () => {
    return (
      <Dialog open={openSalesChannelsDialog} maxWidth="md">
        <Box padding={2} width="650px" maxWidth="100%">
          <Box padding={2}>
            <Alert variant="warning">{t('account.settings.shopify.salesChannelsWarning')}</Alert>
            <Typography variant="body1">
              {t('account.settings.shopify.salesChannelsHelp')}
            </Typography>
            {salesChannelsLoading ? (
              <Box display="flex" padding={3} justifyContent="center">
                <CircularProgress />
              </Box>
            ) : salesChannels.length === 0 ? (
              <p className="pt-3">{t('account.settings.shopify.salesChannelsEmpty')}</p>
            ) : (
              salesChannels.map((channel) => (
                <div
                  className="d-flex flex-row align-items-center justify-content-between mt-2"
                  key={channel.id}
                >
                  <span>{channel.name}</span>
                  <Switch
                    name={channel.name}
                    checked={channel.selected}
                    onChange={() => toggleSalesChannel(channel.id)}
                  />
                </div>
              ))
            )}
          </Box>
          <Box marginTop={1} style={{ float: 'right' }}>
            <Button
              variant="outlined"
              style={{ marginRight: '15px' }}
              onClick={() => setOpenSalesChannelsDialog(false)}
            >
              {t('common.close')}
            </Button>
            <Button
              variant="contained"
              disabled={salesChannelsLoading}
              onClick={() => updateSalesChannels()}
            >
              {t('common.confirm')}
            </Button>
          </Box>
        </Box>
      </Dialog>
    )
  }

  const changePriceModifierDialog = () => {
    return (
      <Dialog open={openPriceModifierDialog} data-testid="price-modifier-dialog">
        <Box padding={2} width="500px" maxWidth="100%">
          <Box padding={2}>
            <Alert variant="warning">{t('stockPricing.changePriceModifierDisclaimer')}</Alert>
            <Typography variant="body1">
              <TextField
                fullWidth
                label={t('stockPricing.changePriceModifier')}
                value={priceModifierValue}
                type="number"
                onChange={(e) => setPriceModifierValue(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">%</InputAdornment>,
                }}
                inputProps={{
                  'data-testid': 'price-modifier-input',
                }}
              />
            </Typography>
          </Box>
          <Box marginTop={1} style={{ float: 'right' }}>
            <Button
              variant="outlined"
              style={{ marginRight: '15px' }}
              onClick={() => closeDialog()}
              data-testid="price-modifier-close-btn"
            >
              {t('common.close')}
            </Button>
            <Button
              variant="contained"
              onClick={() => updatePriceModifier()}
              data-testid="price-modifier-confirm-btn"
            >
              {t('common.confirm')}
            </Button>
          </Box>
        </Box>
      </Dialog>
    )
  }

  return (
    <>
      {changePriceModifierDialog()}
      {salesChannelsDialog()}
      <SalesChannelsConnector
        type="Shopify"
        key={shop}
        title={shop}
        active={true}
        icon="fa-brands fa-shopify"
        buttons={[
          [
            <div
              className="d-flex flex-row align-items-center justify-content-start"
              key="uninstall"
            >
              <i className="fa-light fa-trash"></i>
              <span>{t('common.uninstall')}</span>
            </div>,
            () => (window.location.href = getUninstallLink(shop)),
          ],
          [
            <div
              className="d-flex flex-row align-items-center justify-content-start my-4"
              key="changePriceModifier"
              data-testid="change-price-modifier-btn"
            >
              <i className="fa-light fa-dollar-sign"></i>
              <span>{t('stockPricing.changePriceModifier')}</span>
            </div>,
            () => openDialog(),
          ],
          [
            <div
              className="d-flex flex-row align-items-center justify-content-start"
              key="salesChannels"
            >
              <i className="fa-light fa-store"></i>
              <span>{t('account.settings.shopify.salesChannels')}</span>
            </div>,
            () => openSalesChannels(),
          ],
        ]}
      />
    </>
  )
}
